/**
 * Settings registry catalog (docs/07). The single source of truth for every
 * configurable business value. Defaults mirror production / owner-confirmed
 * rules. The DB `app_settings` table is seeded from this and is the only
 * place these values live at runtime — NO hardcoded business values in code.
 */
import { WHATSAPP_TYPES, whatsappSettingKey, defaultWhatsappConfig } from './whatsapp.js';
export const SETTINGS_CATALOG = [
    // ── Who may create a login (owner 2026-10-08) ──
    // Seven accounts self-registered from one address via curl on 2026-10-08 and
    // were usable within seconds. Self sign-up is now OFF by default and is a
    // switch the owner can see, rather than a door nobody knew was open.
    {
        key: 'auth.self_signup_enabled',
        group: 'Access',
        label: 'Let staff and agents create their own login',
        description: 'OFF (recommended): nobody can sign themselves up — an admin creates every login. '
            + 'When ON, the Sign up form is offered on the login page; a new login still does nothing until '
            + 'an admin approves it in Approvals.',
        type: 'boolean',
        default: false,
        editableBy: 'super_admin',
    },
    // ── NEFT payout sheet (owner-confirmed 2026-07-20) ──
    {
        key: 'payouts.neft_debit_account',
        group: 'Payouts',
        label: 'NEFT debit account number',
        description: "Company account the bank debits for every NEFT row. Blank falls back to the bank master's disbursement account.",
        type: 'string',
        default: '',
        editableBy: 'admin',
    },
    {
        key: 'payouts.neft_beneficiary_email',
        group: 'Payouts',
        label: 'NEFT beneficiary email (fallback)',
        description: "Used in the sheet's Beneficiary Email ID column when a customer has no email on file. Blank leaves the cell empty.",
        type: 'string',
        default: '',
        editableBy: 'admin',
    },
    // ── Interest engine (owner-confirmed 2026-07-16) ──
    {
        key: 'interest.payout_day_of_month',
        group: 'Interest',
        label: 'Interest payout day',
        description: 'Day of month interest is paid (holiday-adjusted backward).',
        type: 'number',
        default: 28,
        editableBy: 'workflow',
    },
    // Owner 2026-10-26, day moved 2026-10-27: investments from the 26th are paid in the next month's batch.
    {
        key: 'interest.next_batch_from_day',
        group: 'Interest',
        label: 'Pay in next month from day',
        description: "Money received on or after this day of the month is not paid in that month's interest batch; "
            + 'its first payment comes in the next month, covering every day from the day the money arrived. 0 = off.',
        type: 'number',
        default: 26,
        editableBy: 'workflow',
    },
    {
        key: 'interest.day_count_convention',
        group: 'Interest',
        label: 'Day-count convention',
        description: 'Actual365 = actual days ÷ 365 every period (29th→28th).',
        type: 'enum',
        options: ['Actual365', 'Thirty360', 'Actual360', 'ActualActual'],
        default: 'Actual365',
        editableBy: 'workflow',
    },
    {
        key: 'interest.cutover_from',
        group: 'Interest',
        label: 'New-convention cutover date',
        description: 'From this date all investments use the new rule; earlier paid interest is frozen.',
        type: 'date',
        default: '2026-07-01',
        editableBy: 'super_admin',
    },
    // ── Incentives (4-cell matrix, flat-or-pct) ──
    {
        key: 'incentive.staff_new_no_referrer',
        group: 'Incentives',
        label: 'Staff % — new customer, no referrer',
        description: 'Enroller incentive when the customer is brand-new and no referrer is named.',
        type: 'rate',
        default: { mode: 'pct', value: 2.0 },
        editableBy: 'workflow',
    },
    {
        key: 'incentive.staff_existing_with_referrer',
        group: 'Incentives',
        label: 'Staff % — existing customer, with referrer',
        description: 'Enroller incentive when the customer already existed and a referrer is named (the referrer earns the repeat rate, not the enroller — usually 0).',
        type: 'rate',
        default: { mode: 'pct', value: 0 },
        editableBy: 'workflow',
    },
    {
        key: 'incentive.referrer_existing_with_referrer',
        group: 'Incentives',
        label: 'Referrer % — existing customer (repeat investment)',
        description: 'Referrer incentive when they bring a repeat investment from an existing Dhanam customer (handover rate).',
        type: 'rate',
        default: { mode: 'pct', value: 0.25 },
        editableBy: 'workflow',
    },
    {
        key: 'incentive.referrer_new_with_referrer_staff',
        group: 'Incentives',
        label: 'Staff % — new customer, with referrer',
        description: 'Enroller incentive when the customer is new and a referrer is named (usually 0).',
        type: 'rate',
        default: { mode: 'pct', value: 0 },
        editableBy: 'workflow',
    },
    {
        key: 'incentive.referrer_new_with_referrer',
        group: 'Incentives',
        label: 'Referrer % — new customer, with referrer',
        description: 'Referrer incentive when they bring a brand-new customer.',
        type: 'rate',
        default: { mode: 'pct', value: 2.0 },
        editableBy: 'workflow',
    },
    {
        key: 'incentive.agent_commission_cap_pct',
        group: 'Incentives',
        label: 'Agent commission cap %',
        description: 'Maximum approvable agent commission rate.',
        type: 'number',
        default: 2.0,
        editableBy: 'admin',
    },
    // ── Redemptions ──
    {
        key: 'redemption.premature_penalty',
        group: 'Redemptions',
        label: 'Premature redemption penalty',
        description: 'Penalty on premature withdrawal (flat ₹ or % of principal). Net = Principal − Penalty.',
        type: 'rate',
        default: { mode: 'pct', value: 1.0 },
        editableBy: 'workflow',
    },
    {
        key: 'redemption.premature_penalty_waiver_enabled',
        group: 'Redemptions',
        label: 'Allow premature penalty waiver / discount',
        description: 'When on, a CXO can waive or reduce the premature-withdrawal penalty while approving.',
        type: 'boolean',
        default: true,
        editableBy: 'admin',
    },
    // ── Numbering ──
    {
        key: 'numbering.customer_format',
        group: 'Numbering',
        label: 'Customer code format',
        description: 'Tokens: {seq:N}, {yyyy}.',
        type: 'string',
        default: 'DHN{seq:6}',
        editableBy: 'super_admin',
    },
    {
        key: 'numbering.application_format',
        group: 'Numbering',
        label: 'Application no. format',
        description: 'Tokens: {seq:N}, {yyyy}.',
        type: 'string',
        default: 'APP-{yyyy}-{seq:6}',
        editableBy: 'super_admin',
    },
    // ── Customers ──
    {
        key: 'customers.lead_sources',
        group: 'Customers',
        label: 'Lead sources',
        description: 'Selectable lead source values.',
        type: 'list',
        default: ['Social Media', 'Walk-IN', 'Agent', 'DhanamFin App', 'Others'],
        editableBy: 'admin',
    },
    {
        key: 'customers.lead_statuses',
        group: 'Customers',
        label: 'Lead statuses',
        description: 'Lead pipeline statuses.',
        type: 'list',
        default: ['New', 'Contacted', 'Interested', 'Follow-up', 'Converted', 'Lost'],
        editableBy: 'admin',
    },
    {
        key: 'customers.collection_methods',
        group: 'Customers',
        label: 'Collection methods',
        description: 'Accepted money-collection methods.',
        type: 'list',
        default: ['NEFT', 'IMPS', 'RTGS', 'Cheque', 'Cash', 'Other'],
        editableBy: 'admin',
    },
    {
        key: 'customers.lead_categories',
        group: 'Customers',
        label: 'Lead categories',
        description: 'Selectable investor-category values in the lead form.',
        type: 'list',
        default: ['Individual', 'HUF', 'Corporate', 'Trust', 'NRI', 'Others'],
        editableBy: 'admin',
    },
    {
        key: 'customers.lead_referred_by',
        group: 'Customers',
        label: 'Lead "Referred by" options',
        description: 'Selectable referral-source values in the lead form.',
        type: 'list',
        default: ['Existing customer', 'Agent', 'Staff', 'Walk-in', 'Advertisement', 'Others'],
        editableBy: 'admin',
    },
    {
        key: 'customers.lead_interested_schemes',
        group: 'Customers',
        label: 'Lead interested schemes',
        description: 'Selectable scheme-interest values in the lead form.',
        // Payout-style schemes, not product names (owner 2026-07-23).
        type: 'list',
        default: ['Monthly', 'Cumulative', 'Double', 'Annual'],
        editableBy: 'admin',
    },
    // ── Portal ──
    {
        key: 'portal.statement_display_cutoff',
        group: 'Portal',
        label: 'Statement display cutoff',
        description: 'Customer-facing lists show rows on/after this date; aggregates stay full.',
        type: 'date',
        default: '2026-06-19',
        editableBy: 'admin',
    },
    {
        key: 'portal.otp_ttl_minutes',
        group: 'Portal',
        label: 'OTP validity (minutes)',
        description: 'How long a portal OTP stays valid.',
        type: 'number',
        default: 10,
        editableBy: 'admin',
    },
    // ── Approvals ──
    {
        key: 'approvals.premature_l2_role',
        group: 'Approvals',
        label: 'Premature redemption approver',
        description: 'Role that approves premature redemptions (owner: CXO).',
        type: 'enum',
        options: ['cxo', 'admin'],
        default: 'cxo',
        editableBy: 'super_admin',
    },
    {
        key: 'approvals.subscription_maker_checker',
        group: 'Approvals',
        label: 'Require approval at application creation',
        description: 'When on, a new application needs a subscription approval before collection (old-app optional gate). Off by default — the allotment approval is the gate.',
        type: 'boolean',
        default: false,
        editableBy: 'super_admin',
    },
    // ── Escrow (inbound subscription reconciliation) ──
    {
        key: 'escrow.account_number',
        group: 'Escrow',
        label: 'Escrow account number',
        description: 'The SBI current account NCD subscription money lands in. Used to sanity-check an uploaded statement is for the right account.',
        type: 'string',
        default: '',
        editableBy: 'admin',
    },
    {
        key: 'escrow.company_accounts',
        group: 'Escrow',
        label: 'Company-owned accounts (comma-separated)',
        description: "Account numbers that belong to the company itself. Credits from these are the escrow floor / internal transfers, not investments.",
        type: 'string',
        default: '',
        editableBy: 'admin',
    },
    {
        key: 'escrow.company_floor',
        group: 'Escrow',
        label: 'Company floor balance (₹)',
        description: 'Minimum balance the company parks in escrow (not an investment). Shown separately in the escrow breakup.',
        type: 'number',
        default: 1000000,
        editableBy: 'admin',
    },
    // ── Reports (recipient lists, super-admin managed — owner 2026-08-01) ──
    {
        key: 'reports.book_summary_send_time',
        group: 'Reports',
        label: 'Daily summary send time (IST, HH:MM)',
        description: '24-hour IST time the daily book summary is emailed, e.g. 18:30. It fires within ~15 minutes of this time. Invalid values fall back to 18:00.',
        type: 'string',
        default: '18:30',
        editableBy: 'super_admin',
    },
    {
        key: 'reports.book_summary_recipients',
        group: 'Reports',
        label: 'Daily book summary recipients',
        description: 'Email addresses that receive the 6:30 PM daily book summary. Leave empty to fall back to the management roles (super-admin, admin, CXO, NCD manager).',
        type: 'list',
        default: [],
        editableBy: 'super_admin',
    },
    {
        key: 'reports.digest_recipients',
        group: 'Reports',
        label: 'Daily ops digest recipients',
        description: 'Email addresses that receive the 5:00 PM digest of things needing a decision — bank accounts that cannot be paid, work that has stalled, data that looks wrong, and open questions. It is sent EVERY day, including when there is nothing to report, so that silence means the job has broken rather than that all is well. Leave empty to fall back to the super-admins.',
        type: 'list',
        default: [],
        editableBy: 'super_admin',
    },
    {
        key: 'reports.digest_enabled',
        group: 'Reports',
        label: 'Send the daily ops digest',
        description: 'On by default once configured. Turn off to stop the 5:00 PM digest without clearing the recipient list.',
        type: 'boolean',
        default: true,
        editableBy: 'super_admin',
    },
    {
        key: 'reports.digest_time',
        group: 'Reports',
        label: 'Ops digest send time (IST, HH:MM)',
        description: '24-hour IST time the digest is sent, e.g. 17:00. It fires within ~15 minutes of this time. Invalid values fall back to 17:00.',
        type: 'string',
        default: '17:00',
        editableBy: 'super_admin',
    },
    {
        key: 'reports.error_alert_recipients',
        group: 'Reports',
        label: 'Error / 500 alert recipients',
        description: 'Email addresses that receive server error (5xx) and crash alerts. Leave empty to fall back to the OPS_ALERT_EMAILS server setting.',
        type: 'list',
        default: [],
        editableBy: 'super_admin',
    },
    {
        key: 'reports.transactions_email_enabled',
        group: 'Reports',
        label: 'Email the transaction register daily',
        description: 'When on, the Transactions sheet (every addition and deletion, the same tab as in the NCD book) is emailed once a day to the recipients below. Off by default so turning it on is a deliberate act.',
        type: 'boolean',
        default: false,
        editableBy: 'super_admin',
    },
    {
        key: 'reports.transactions_email_time',
        group: 'Reports',
        label: 'Transaction register send time (IST, HH:MM)',
        description: '24-hour IST time the daily transaction register is emailed, e.g. 19:00. It fires within ~15 minutes of this time. Invalid values fall back to 19:00.',
        type: 'string',
        default: '19:00',
        editableBy: 'super_admin',
    },
    {
        key: 'reports.transactions_email_recipients',
        group: 'Reports',
        label: 'Transaction register recipients',
        description: 'Email addresses that receive the daily transaction register. Nothing is sent while this is empty — there is no role fallback, because this attachment carries the whole book.',
        type: 'list',
        default: [],
        editableBy: 'super_admin',
    },
    {
        key: 'reports.backup_check_recipients',
        group: 'Reports',
        label: 'Nightly backup-check recipients',
        description: 'Email addresses that receive the nightly backup-status email. Leave empty to fall back to the super-admin and admin roles.',
        type: 'list',
        default: [],
        editableBy: 'super_admin',
    },
    // ── TDS threshold (owner 2026-08-07) ──
    {
        key: 'tds.threshold_amount',
        group: 'TDS',
        label: 'TDS applicability threshold (₹)',
        description: "When a customer's outstanding NCD book crosses this, they become TDS-applicable and an approval is raised to recover TDS on interest already paid.",
        type: 'number',
        default: 3000000,
        editableBy: 'admin',
    },
    {
        key: 'tds.rate_with_pan_pct',
        group: 'TDS',
        label: 'TDS rate — PAN on file (%)',
        description: 'Rate applied to already-paid interest when recovering TDS, for a customer with a valid PAN.',
        type: 'number',
        default: 10,
        editableBy: 'admin',
    },
    {
        key: 'tds.rate_without_pan_pct',
        group: 'TDS',
        label: 'TDS rate — no PAN (%)',
        description: 'Higher rate applied when the customer has no PAN on file.',
        type: 'number',
        default: 20,
        editableBy: 'admin',
    },
    // ── System ──
    {
        key: 'system.api_page_limit_max',
        group: 'System',
        label: 'Max page size',
        description: 'Upper bound on list page size.',
        type: 'number',
        default: 500,
        editableBy: 'admin',
    },
    // ── WhatsApp templates (owner 2026-08-25) ──
    // One JSON config per message type: which approved WappCloud template it uses,
    // whether it is on, and which data field feeds each {{n}}. Rendered by a
    // dedicated editor on the Settings page, not the generic control. The message
    // WORDING is not here — that is approved inside WappCloud/Meta and referenced
    // by name. See packages/shared/src/whatsapp.ts.
    ...WHATSAPP_TYPES.map((def) => ({
        key: whatsappSettingKey(def.type),
        group: 'WhatsApp',
        label: `${def.label} template`,
        description: `Approved WappCloud template, on/off and {{n}} variable mapping for the ${def.label.toLowerCase()} message.`,
        type: 'json',
        default: defaultWhatsappConfig(def),
        editableBy: 'admin',
    })),
    // The company's authorised signatory who counter-signs each locker agreement
    // after the customer (owner 2026-09-10). Their e-Sign link goes to this phone.
    {
        key: 'lockers.agreement_signatory_name',
        group: 'Lockers',
        label: 'Locker agreement — authorised signatory',
        description: 'Name of the company signatory (CEO) who counter-signs each locker agreement.',
        type: 'string',
        default: 'Saravana Suren',
        editableBy: 'admin',
    },
    {
        key: 'lockers.agreement_signatory_phone',
        group: 'Lockers',
        label: 'Locker agreement — signatory phone',
        description: 'Mobile number the signatory’s e-Sign link is sent to.',
        type: 'string',
        default: '8925803983',
        editableBy: 'admin',
    },
];
export function settingDefaults() {
    return Object.fromEntries(SETTINGS_CATALOG.map((s) => [s.key, s.default]));
}
//# sourceMappingURL=settings.js.map